import { HomeDetailsResponse } from 'common/api/crm/home/models/HomeDetailsResponse';

/**
 * Builds user segments for Constructor API calls from home details
 * @param homeDetails - User's home details containing organization and profile information
 * @returns Array of user segment strings in format "key:value"
 */
export function buildUserSegments(homeDetails: HomeDetailsResponse | null): string[] {
    if (!homeDetails || !homeDetails.defaultOrganization) {
        return [];
    }

    const segments: string[] = [];
    const { defaultOrganization } = homeDetails;

    // Organization ID
    if (defaultOrganization.id) {
        segments.push(`organization_id:${defaultOrganization.id}`);
    }

    // Organization type
    if (defaultOrganization.type) {
        segments.push(`organization_type:${defaultOrganization.type}`);
    }

    // Organization zip code
    if (defaultOrganization.address?.zipCode) {
        segments.push(`organization_zip:${defaultOrganization.address.zipCode}`);
    }

    // Organization state
    if (defaultOrganization.address?.state) {
        segments.push(`organization_state:${defaultOrganization.address.state}`);
    }

    // Participant enrollment date and type (new vs existing)
    const enrollmentDate = getParticipantEnrollmentDate(homeDetails);
    if (enrollmentDate) {
        segments.push(`participant_enrolled_date:${enrollmentDate}`);

        const participantType = isNewParticipant(homeDetails, new Date()) ? 'new' : 'existing';
        segments.push(`participant_type:${participantType}`);
    }

    return segments;
}

/**
 * Gets the participant enrollment date from home details
 * Uses the createDate from the organization membership
 */
function getParticipantEnrollmentDate(homeDetails: HomeDetailsResponse): string | null {
    if (!homeDetails.selectedOrgId || !homeDetails.organizations[homeDetails.selectedOrgId]) {
        return null;
    }

    const membership = homeDetails.organizations[homeDetails.selectedOrgId];
    return membership.createDate || null;
}

const DAY_MS = 1000 * 60 * 60 * 24;

/** Enrolled in the selected group 60 whole days ago or less; no or an invalid date is not new. */
export const isNewParticipant = (homeDetails: HomeDetailsResponse, now: Date) => {
    const enrolledAt = Date.parse(getParticipantEnrollmentDate(homeDetails) ?? '');
    return Math.floor(Math.abs(now.getTime() - enrolledAt) / DAY_MS) <= 60;
};
